import type { AllowedImageMime } from './limits.ts';
import type { Bytes } from '../model/binary.ts';

/**
 * How big an image says it is, read from its header and without decoding a single pixel.
 *
 * A byte limit only bounds the *compressed* file, and compression is precisely what an attacker
 * controls. A 40 kB PNG can declare itself 30000×30000; nothing has gone wrong until the browser
 * is asked to draw it, at which point it allocates 3.6 GB and the tab dies. The same trick works
 * with an animated GIF that is mostly identical frames.
 *
 * So the dimensions are read from the header, which every one of these formats puts near the front
 * and states as plain integers. It costs a few dozen bytes of reading and forecloses the whole
 * class of attack.
 *
 * Every read is bounds-checked and returns null rather than throwing: this runs on bytes from a
 * file somebody sent, and a truncated header is exactly what a hostile one looks like.
 */
export interface ImageHeader {
  readonly width: number;
  readonly height: number;
  /** 1 for a still image. More for animated GIF and WebP, and for APNG. */
  readonly frames: number;
}

export function readImageHeader(mimeType: AllowedImageMime, bytes: Bytes): ImageHeader | null {
  switch (mimeType) {
    case 'image/png':
      return readPng(bytes);
    case 'image/gif':
      return readGif(bytes);
    case 'image/jpeg':
      return readJpeg(bytes);
    case 'image/webp':
      return readWebp(bytes);
  }
}

/** Big-endian unsigned 32-bit, or null when the bytes are not there. */
function be32(bytes: Bytes, offset: number): number | null {
  const [a, b, c, d] = [bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]];
  if (a === undefined || b === undefined || c === undefined || d === undefined) {
    return null;
  }
  return ((a << 24) | (b << 16) | (c << 8) | d) >>> 0;
}

function be16(bytes: Bytes, offset: number): number | null {
  const [a, b] = [bytes[offset], bytes[offset + 1]];
  return a === undefined || b === undefined ? null : (a << 8) | b;
}

function le16(bytes: Bytes, offset: number): number | null {
  const [a, b] = [bytes[offset], bytes[offset + 1]];
  return a === undefined || b === undefined ? null : a | (b << 8);
}

function le32(bytes: Bytes, offset: number): number | null {
  const [a, b, c, d] = [bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]];
  if (a === undefined || b === undefined || c === undefined || d === undefined) {
    return null;
  }
  return (a | (b << 8) | (c << 16) | (d << 24)) >>> 0;
}

/** Little-endian unsigned 24-bit, read as three bytes rather than a masked 32. */
function le24(bytes: Bytes, offset: number): number | null {
  const [a, b, c] = [bytes[offset], bytes[offset + 1], bytes[offset + 2]];
  return a === undefined || b === undefined || c === undefined ? null : a | (b << 8) | (c << 16);
}

function chunkName(bytes: Bytes, offset: number): string | null {
  const four = bytes.subarray(offset, offset + 4);
  return four.length === 4 ? String.fromCharCode(...four) : null;
}

/**
 * PNG: the IHDR chunk is required to come first, so the dimensions sit at a fixed offset.
 *
 * The chunk walk that follows is only looking for `acTL`, which is what makes a PNG an APNG and
 * says how many frames it has.
 */
function readPng(bytes: Bytes): ImageHeader | null {
  if (chunkName(bytes, 12) !== 'IHDR') {
    return null;
  }
  const width = be32(bytes, 16);
  const height = be32(bytes, 20);
  if (width === null || height === null || width === 0 || height === 0) {
    return null;
  }

  let frames = 1;
  // Chunks start after the 8-byte signature; each is length, type, data, CRC.
  let offset = 8;
  while (offset + 8 <= bytes.length) {
    const length = be32(bytes, offset);
    const name = chunkName(bytes, offset + 4);
    if (length === null || name === null) {
      break;
    }
    if (name === 'acTL') {
      frames = be32(bytes, offset + 8) ?? 1;
      break;
    }
    if (name === 'IDAT' || name === 'IEND') {
      // Past here an animation control chunk would be illegal, so there is nothing left to find.
      break;
    }
    offset += 12 + length;
  }

  return { width, height, frames: Math.max(1, frames) };
}

/**
 * GIF: the logical screen descriptor is at a fixed offset, and frames are counted by walking the
 * block structure — reading each block's length and skipping it, never decoding anything.
 */
function readGif(bytes: Bytes): ImageHeader | null {
  const width = le16(bytes, 6);
  const height = le16(bytes, 8);
  if (width === null || height === null || width === 0 || height === 0) {
    return null;
  }

  let offset = 13;
  // A global colour table, if present, sits between the descriptor and the first block.
  const packed = bytes[10];
  if (packed !== undefined && (packed & 0x80) !== 0) {
    offset += 3 * 2 ** ((packed & 0x07) + 1);
  }

  let frames = 0;
  while (offset < bytes.length) {
    const marker = bytes[offset];
    if (marker === 0x3b || marker === undefined) {
      break;
    }

    if (marker === 0x21) {
      // Extension: a label, then a chain of sub-blocks each prefixed with its own length.
      offset += 2;
      offset = skipSubBlocks(bytes, offset);
      continue;
    }

    if (marker === 0x2c) {
      frames += 1;
      const local = bytes[offset + 9];
      offset += 10;
      if (local !== undefined && (local & 0x80) !== 0) {
        offset += 3 * 2 ** ((local & 0x07) + 1);
      }
      // The LZW minimum code size, then the image data as sub-blocks.
      offset += 1;
      offset = skipSubBlocks(bytes, offset);
      continue;
    }

    // Not a marker this format defines. Reading further would be guessing.
    return null;
  }

  return { width, height, frames: Math.max(1, frames) };
}

function skipSubBlocks(bytes: Bytes, start: number): number {
  let offset = start;
  while (offset < bytes.length) {
    const size = bytes[offset];
    if (size === undefined || size === 0) {
      return offset + 1;
    }
    offset += size + 1;
  }
  return offset;
}

/**
 * JPEG: walk the marker segments to the start-of-frame, which is where the size lives.
 *
 * Progressive and arithmetic-coded variants have their own SOF markers, and all of them lay the
 * dimensions out the same way. `DHT`, `DAC` and `DRI` share the numeric range but are not frames,
 * which is why the list is explicit rather than a range.
 */
function readJpeg(bytes: Bytes): ImageHeader | null {
  const START_OF_FRAME = new Set([
    0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
  ]);

  let offset = 2;
  while (offset + 4 <= bytes.length) {
    if (bytes[offset] !== 0xff) {
      return null;
    }
    const marker = bytes[offset + 1];
    if (marker === undefined) {
      return null;
    }
    // Padding: any number of 0xFF bytes may precede a marker.
    if (marker === 0xff) {
      offset += 1;
      continue;
    }
    // Start of scan: the entropy-coded data begins and there is no frame header after it.
    if (marker === 0xda) {
      return null;
    }

    const length = be16(bytes, offset + 2);
    if (length === null || length < 2) {
      return null;
    }

    if (START_OF_FRAME.has(marker)) {
      const height = be16(bytes, offset + 5);
      const width = be16(bytes, offset + 7);
      if (width === null || height === null || width === 0 || height === 0) {
        return null;
      }
      return { width, height, frames: 1 };
    }

    offset += 2 + length;
  }

  return null;
}

/**
 * WebP: three containers with three different headers.
 *
 * `VP8 ` is lossy, `VP8L` lossless, and `VP8X` the extended form that carries animation. Only the
 * extended one can be animated, and its frames are `ANMF` chunks.
 */
function readWebp(bytes: Bytes): ImageHeader | null {
  const kind = chunkName(bytes, 12);

  if (kind === 'VP8 ') {
    // A 3-byte frame tag, then the start code, then 14-bit dimensions.
    const width = le16(bytes, 26);
    const height = le16(bytes, 28);
    if (width === null || height === null) {
      return null;
    }
    return sized(width & 0x3f_ff, height & 0x3f_ff, 1);
  }

  if (kind === 'VP8L') {
    const packed = le32(bytes, 21);
    if (packed === null) {
      return null;
    }
    return sized((packed & 0x3f_ff) + 1, ((packed >>> 14) & 0x3f_ff) + 1, 1);
  }

  if (kind !== 'VP8X') {
    return null;
  }

  // Extended: 24-bit little-endian canvas size, each stored as the value minus one. Read as three
  // bytes rather than as a masked 32-bit word, which would need one byte more than the smallest
  // valid file actually has.
  const w = le24(bytes, 24);
  const h = le24(bytes, 27);
  if (w === null || h === null) {
    return null;
  }
  const width = w + 1;
  const height = h + 1;

  let frames = 0;
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const name = chunkName(bytes, offset);
    const length = le32(bytes, offset + 4);
    if (name === null || length === null) {
      break;
    }
    if (name === 'ANMF') {
      frames += 1;
    }
    // Chunks are padded to an even length.
    offset += 8 + length + (length % 2);
  }

  return sized(width, height, Math.max(1, frames));
}

function sized(width: number, height: number, frames: number): ImageHeader | null {
  return width > 0 && height > 0 ? { width, height, frames } : null;
}
